/**
 * BYOB (Build Your Own Bundle) builder — package-tile pattern.
 *
 * - Hidden until `catchy:variant-change` (from product-catchy.js) delivers a
 *   resolved variant; then shows "Catchy Only" / "Catchy Bundle" tiles.
 * - "Catchy Only" (default): theme buy buttons untouched — stock behavior.
 * - "Catchy Bundle": expands the accessory panel and hides the theme buy
 *   buttons (incl. accelerated checkout, which can only buy the hero variant)
 *   via the `byob-mode-hidden` class — deliberately separate from the theme's
 *   own `hidden` class, which product-catchy.js toggles for the "Other" chair
 *   flow. The bundle ATC adds everything in one /cart/add.js call and fires
 *   the theme's `cart:update` event so the cart drawer refreshes/opens.
 * - Accessory visibility: chair-specific metaobject sets are exclusive; all
 *   other chairs get the default/universal set.
 *
 * Pricing note: tier percentages shown here are presentational — actual
 * discounting is enforced by automatic discounts / a discount function.
 */
(function () {
  if (customElements.get('byob-builder')) return;

  class ByobBuilder extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-role="panel"]');
      this.grid = this.querySelector('[data-role="grid"]');
      this.moreEl = this.querySelector('[data-role="more"]');
      this.moreGrid = this.querySelector('[data-role="more-grid"]');
      this.moreMetaEl = this.querySelector('[data-role="more-meta"]');
      this.modal = this.querySelector('[data-role="modal"]');
      this.modalCard = null;
      this.productCache = {};
      this.atcButton = this.querySelector('[data-role="atc"]');
      this.atcLabelEl = this.querySelector('[data-role="atc-label"]');
      this.hintEl = this.querySelector('[data-role="tier-hint"]');
      this.fillEl = this.querySelector('[data-role="tier-fill"]');
      this.errorEl = this.querySelector('[data-role="error"]');
      this.soloPriceEl = this.querySelector('[data-role="solo-price"]');
      this.bundlePriceEl = this.querySelector('[data-role="bundle-price"]');

      this.heroVariant = null;
      this.mode = 'solo';
      this.atcLabelTemplate = this.atcLabelEl ? this.atcLabelEl.textContent : 'Add bundle to cart';
      this.maxQty = parseInt(this.dataset.maxQty, 10) || 5;
      this.showSavings = this.dataset.showSavings === 'true';
      // Threshold is set in the market's currency (major units); compare in cents.
      this.freeShipThreshold = (parseFloat(this.dataset.freeShippingThreshold) || 0) * 100;
      this.freeShipLabel = this.dataset.freeShippingLabel || 'FREE shipping';

      const qtys = (this.dataset.tierQtys || '').split(',').map(Number);
      const pcts = (this.dataset.tierPcts || '').split(',').map(Number);
      this.tiers = qtys
        .map((qty, i) => ({ qty, pct: pcts[i] }))
        .filter((t) => t.qty > 0 && t.pct > 0)
        .sort((a, b) => a.qty - b.qty);

      try {
        this.sets = JSON.parse(this.querySelector('script[data-byob-sets]').textContent);
      } catch (e) {
        this.sets = [];
      }

      this.formatter = null;
      try {
        this.formatter = new Intl.NumberFormat(this.dataset.locale || undefined, {
          style: 'currency',
          currency: this.dataset.currency || 'AUD',
        });
      } catch (e) {
        /* fall back to raw numbers */
      }

      this.cards = Array.from(this.grid ? this.grid.querySelectorAll('.byob-card') : []).map((el) => {
        let variants = [];
        try {
          variants = JSON.parse(el.querySelector('script[data-byob-variants]').textContent);
        } catch (e) {
          /* card without variant data is ignored at add-to-cart time */
        }
        return {
          el,
          handle: el.dataset.handle,
          variants,
          qty: 0,
          qtyEl: el.querySelector('[data-role="qty"]'),
          priceEl: el.querySelector('[data-role="card-price"]'),
          imageEl: el.querySelector('[data-role="card-image"]'),
          selectEl: el.querySelector('[data-role="card-select"]'),
        };
      });

      // Position tier markers proportionally to the highest tier quantity
      const maxTierQty = this.tiers.length ? this.tiers[this.tiers.length - 1].qty : 1;
      this.querySelectorAll('.byob-tiers__marker').forEach((marker) => {
        const qty = Number(marker.dataset.tierQty);
        if (qty > 0) marker.style.left = Math.min(100, (qty / maxTierQty) * 100) + '%';
      });

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      if (this.modal) {
        // Covers every close path incl. native Esc handling
        this.modal.addEventListener('close', () => {
          this.modal.classList.remove('is-open');
          this.modalCard = null;
        });

        // Keep the active thumbnail in sync while the main image is swiped
        const track = this.modal.querySelector('[data-role="modal-track"]');
        if (track) {
          let ticking = false;
          track.addEventListener('scroll', () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
              ticking = false;
              const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
              this.setActiveThumb(index);
            });
          });
        }
      }
      document.addEventListener('catchy:variant-change', (event) => {
        this.setHeroVariant(event.detail ? event.detail.variant : null);
      });

      // "Catchy Only" path: the customer adds via the theme's native buy button
      // (not our bundle ATC), which dispatches the theme's cart-add event with
      // source 'product-form-component'. If that happens while we're NOT in
      // bundle mode and a variant is selected, count it as a solo add-to-cart —
      // the "bought just the Catchy, skipped BYOB" branch of the funnel.
      document.addEventListener('cart:update', (event) => {
        const data = event.detail && event.detail.data;
        if (!data || data.source !== 'product-form-component') return;
        if (this.mode !== 'bundle' && this.heroVariant && !data.didError) {
          this.track('byob_solo_add_to_cart', {
            hero_variant_id: this.heroVariant.id,
            bundle_value: this.heroVariant.price / 100,
            currency: this.dataset.currency || 'AUD',
          });
        }
      });

      // Always start locked, even if Shopify has auto-resolved a default
      // variant (it always does — product.selected_or_first_available_variant
      // — which pre-checks a radio in the picker markup). The rest of the
      // custom picker deliberately ignores that default and requires an
      // explicit selection (see the disabled init calls in product-catchy.js);
      // this widget must match that behavior, so it only ever reacts to the
      // real `catchy:variant-change` event fired by an actual user action.
      this.setHeroVariant(null);
    }

    formatMoney(cents) {
      if (!this.formatter) return (cents / 100).toFixed(2);
      return this.formatter.format(cents / 100);
    }

    heroColor() {
      if (!this.heroVariant) return null;
      return this.heroVariant.option3 || this.heroVariant.option2 || null;
    }

    /**
     * Fires an analytics event to every destination that's present, guarded so
     * a missing platform can never throw:
     *  - window.dataLayer  → GA4 / Google Tag Manager
     *  - Shopify.analytics.publish → Shopify Customer Events (feeds Meta / TikTok
     *    / GA4 web pixels that subscribe to them)
     *  - Klaviyo → so bundle activity can trigger email/SMS flows
     * Event names are prefixed `byob_`.
     */
    track(name, payload) {
      const data = { source: 'byob-builder', chair: this.heroVariant ? this.heroVariant.option1 : null, ...payload };
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: name, ...data });
      } catch (e) {
        /* no GTM/GA4 */
      }
      try {
        if (window.klaviyo && typeof window.klaviyo.track === 'function') {
          window.klaviyo.track(name, data);
        } else if (window._learnq && typeof window._learnq.push === 'function') {
          window._learnq.push(['track', name, data]);
        }
      } catch (e) {
        /* no Klaviyo */
      }
      // GA4 straight from the theme (Measurement Protocol). Runs here — not in a
      // sandboxed pixel — so it can read the real `_ga` cookie and use the true
      // GA4 client id, which is what makes events show up AND join the funnel.
      this.sendGA4(name, data);
    }

    /** Reads a browser cookie by name (returns '' if absent). */
    static cookie(name) {
      const m = document.cookie.match('(?:^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
      return m ? decodeURIComponent(m[1]) : '';
    }

    /** Sends one event to GA4 via the Measurement Protocol. No-op until a secret is configured. */
    sendGA4(name, params) {
      const mid = this.dataset.ga4Id;
      const secret = this.dataset.ga4Secret;
      if (!mid || !secret) return; // not configured yet

      // Respect analytics consent where Shopify's Customer Privacy API exists.
      try {
        const cp = window.Shopify && window.Shopify.customerPrivacy;
        if (cp && typeof cp.analyticsProcessingAllowed === 'function' && cp.analyticsProcessingAllowed() === false) {
          return;
        }
      } catch (e) {
        /* proceed if the API isn't present */
      }

      const ga = ByobBuilder.cookie('_ga'); // GA1.1.<clientId....>
      if (!ga) return; // GA4 hasn't set its cookie (not loaded / no consent)
      const gp = ga.split('.');
      const clientId = gp.length >= 4 ? gp[2] + '.' + gp[3] : ga;

      const gs = ByobBuilder.cookie('_ga_' + mid.replace('G-', '')); // GS1.1.<sessionId>...
      const sessionId = gs ? gs.split('.')[2] : undefined;

      const p = { engagement_time_msec: 1 };
      if (sessionId) p.session_id = sessionId;
      Object.keys(params).forEach((k) => {
        if (params[k] !== undefined && params[k] !== null) p[k] = params[k];
      });

      try {
        // No Content-Type header on purpose: it keeps this a "simple" request
        // so the browser skips the CORS preflight that GA's Measurement Protocol
        // endpoint rejects. GA4 still parses the JSON body from a text/plain POST.
        fetch(
          'https://www.google-analytics.com/mp/collect?measurement_id=' +
            encodeURIComponent(mid) + '&api_secret=' + encodeURIComponent(secret),
          {
            method: 'POST',
            keepalive: true,
            body: JSON.stringify({ client_id: clientId, events: [{ name: name, params: p }] }),
          }
        );
      } catch (e) {
        /* network/analytics failure must never affect the storefront */
      }
    }

    /** Solo ↔ bundle package switching. Bundle mode swaps the theme buy buttons for the bundle ATC. */
    setMode(mode) {
      const changed = this.mode !== mode;
      this.mode = mode;
      const bundle = mode === 'bundle';

      if (this.panel) this.panel.hidden = !bundle;

      this.querySelectorAll('.byob-package').forEach((tile) => {
        const active = tile.dataset.package === mode;
        tile.classList.toggle('is-active', active);
        tile.setAttribute('aria-pressed', String(active));
      });

      document.querySelectorAll('.buy-buttons-block').forEach((el) => {
        el.classList.toggle('byob-mode-hidden', bundle);
      });

      if (bundle && changed) {
        this.track('byob_bundle_opened', { hero_variant_id: this.heroVariant ? this.heroVariant.id : null });
      }

      this.recompute();
    }

    setHeroVariant(variant) {
      this.heroVariant = variant || null;
      this.classList.toggle('is-locked', !this.heroVariant);

      // No variant (cleared search / "Other" chair): reset to solo so the
      // theme buy buttons are never left hidden by bundle mode.
      if (!this.heroVariant) {
        if (this.mode !== 'solo') this.setMode('solo');
        this.filterByChair(null);
        this.recompute();
        return;
      }

      // Fire once per page-load, the first time a full variant resolves and the
      // two package tiles become visible — this is the "reached the tiles"
      // denominator for the bundle-tile click-through rate.
      if (!this.tilesShownFired) {
        this.tilesShownFired = true;
        this.track('byob_tiles_shown', { hero_variant_id: this.heroVariant.id });
      }

      const parts = [this.heroVariant.option1, this.heroVariant.option2, this.heroVariant.option3].filter(Boolean);
      const variantEl = this.querySelector('[data-role="hero-variant"]');
      const priceEl = this.querySelector('[data-role="hero-price"]');
      const imageEl = this.querySelector('[data-role="hero-image"]');

      if (variantEl) variantEl.textContent = parts.join(' · ');
      if (priceEl) priceEl.textContent = this.formatMoney(this.heroVariant.price);
      if (this.soloPriceEl) this.soloPriceEl.textContent = this.formatMoney(this.heroVariant.price);

      const media = this.heroVariant.featured_media;
      const src = media && (media.preview_image ? media.preview_image.src : media.src);
      if (imageEl && src) imageEl.src = src;

      this.matchAccessoryColors();
      this.filterByChair(this.heroVariant.option1);
      this.recompute();
    }

    /**
     * Lays out accessory cards for the selected chair in two groups:
     *
     * - Chair WITH a specific set (Stokke Tripp Trapp, IKEA Antilop): the
     *   specific set fills the primary grid, and the default/universal set
     *   moves into the collapsed "More accessories" accordion beneath it —
     *   available (so tier thresholds stay reachable) without forcing a
     *   long scroll.
     * - Any other chair: the universal set renders flat in the primary grid
     *   and the accordion stays hidden.
     *
     * Cards physically move between the two grids; `order` sorts within each.
     * A product present in both a specific and the universal set only ever
     * appears once, in the primary grid.
     */
    filterByChair(chair) {
      const norm = (s) => (s || '').trim().toLowerCase();
      const byPriority = (a, b) => (a.priority || 100) - (b.priority || 100);

      const specificSets = this.sets
        .filter((set) => {
          if (set.default) return false;
          if (!chair || !Array.isArray(set.chairs)) return false;
          return set.chairs.some((c) => norm(c) === norm(chair));
        })
        .sort(byPriority);
      const defaultSets = this.sets.filter((set) => set.default).sort(byPriority);

      const primaryHandles = [];
      const moreHandles = [];
      const collect = (sets, target, exclude) => {
        sets.forEach((set) => {
          (set.products || []).forEach((handle) => {
            if (!target.includes(handle) && !(exclude && exclude.includes(handle))) target.push(handle);
          });
        });
      };

      if (specificSets.length) {
        collect(specificSets, primaryHandles);
        collect(defaultSets, moreHandles, primaryHandles);
      } else {
        collect(defaultSets, primaryHandles);
      }

      const hasMore = moreHandles.length > 0 && this.moreEl && this.moreGrid;
      if (this.moreEl) {
        this.moreEl.hidden = !hasMore;
        this.moreEl.open = false; // collapse again on every chair change
      }

      this.cards.forEach((card) => {
        const primaryIndex = primaryHandles.indexOf(card.handle);
        const moreIndex = hasMore ? moreHandles.indexOf(card.handle) : -1;
        const visible = primaryIndex !== -1 || moreIndex !== -1;

        const targetGrid = moreIndex !== -1 ? this.moreGrid : this.grid;
        if (targetGrid && card.el.parentElement !== targetGrid) targetGrid.appendChild(card.el);

        card.el.hidden = !visible;
        card.el.style.order = visible ? Math.max(primaryIndex, moreIndex) : 0;
        if (!visible && card.qty > 0) {
          card.qty = 0;
          if (card.qtyEl) card.qtyEl.textContent = '0';
          card.el.classList.remove('is-selected');
        }
      });
    }

    /** Defaults each accessory's variant select to the hero colour when an exact option match exists. */
    matchAccessoryColors() {
      const color = this.heroColor();
      if (!color) return;
      const norm = (s) => (s || '').trim().toLowerCase();

      this.cards.forEach((card) => {
        if (!card.selectEl || card.qty > 0) return;
        const match = card.variants.find(
          (v) => v.available && Array.isArray(v.options) && v.options.some((o) => norm(o) === norm(color))
        );
        if (match) {
          card.selectEl.value = String(match.id);
          this.applyCardVariant(card);
        }
      });
    }

    selectedVariant(card) {
      if (card.selectEl) {
        const id = Number(card.selectEl.value);
        return card.variants.find((v) => v.id === id) || null;
      }
      return card.variants.find((v) => v.available) || card.variants[0] || null;
    }

    applyCardVariant(card) {
      const variant = this.selectedVariant(card);
      if (!variant) return;
      if (card.priceEl) card.priceEl.textContent = variant.price_formatted || this.formatMoney(variant.price);
      if (card.imageEl && variant.image) card.imageEl.src = variant.image;
    }

    onClick(event) {
      const tile = event.target.closest('.byob-package');
      if (tile) {
        this.setMode(tile.dataset.package === 'bundle' ? 'bundle' : 'solo');
        return;
      }

      // "Change" on the hero row → scroll back up to the chair/colour picker
      if (event.target.closest('[data-role="hero-edit"]')) {
        const picker =
          document.querySelector('custom-variant-picker') ||
          document.querySelector('.custom-variant-picker') ||
          document.querySelector('.custom-option--high-chair');
        // Scroll up to the picker only — do NOT focus the search input, as that
        // auto-opens the dropdown; let the customer open it themselves.
        if (picker) {
          picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      const button = event.target.closest('[data-action]');
      if (button) {
        const card = this.cards.find((c) => c.el.contains(button));
        if (!card) return;
        const delta = button.dataset.action === 'plus' ? 1 : -1;
        this.setCardQty(card, card.qty + delta);
        return;
      }

      // Quick-view: opened by tapping the accessory thumbnail or its title
      const detailsTrigger = event.target.closest('.byob-card__media, .byob-card__title');
      if (detailsTrigger) {
        const card = this.cards.find((c) => c.el.contains(detailsTrigger));
        if (card) this.openModal(card);
        return;
      }

      if (this.modal && this.modal.open) {
        if (event.target.closest('[data-role="modal-close"]')) {
          this.closeModal();
          return;
        }
        if (event.target.closest('[data-role="modal-add"]')) {
          if (this.modalCard) this.setCardQty(this.modalCard, this.modalCard.qty + 1);
          this.closeModal();
          return;
        }
        const thumb = event.target.closest('.byob-modal__thumb');
        if (thumb) {
          this.goToSlide(Number(thumb.dataset.index));
          return;
        }
        // Native dialog: a click on the backdrop targets the dialog itself
        if (event.target === this.modal) this.closeModal();
        return;
      }

      if (event.target.closest('[data-role="atc"]')) {
        this.addToCart();
      }
    }

    setCardQty(card, qty) {
      const prev = card.qty;
      card.qty = Math.min(this.maxQty, Math.max(0, qty));
      if (card.qtyEl) card.qtyEl.textContent = String(card.qty);
      card.el.classList.toggle('is-selected', card.qty > 0);
      if (card.qty > prev) {
        const variant = this.selectedVariant(card);
        this.track('byob_item_added', {
          product_handle: card.handle,
          product_title: card.el.querySelector('.byob-card__title')?.textContent || card.handle,
          variant_id: variant ? variant.id : null,
        });
      }
      this.recompute();
    }

    /* --- Quick-view modal --- */

    async openModal(card) {
      if (!this.modal) return;

      const titleEl = this.modal.querySelector('[data-role="modal-title"]');
      const priceEl = this.modal.querySelector('[data-role="modal-price"]');
      const descEl = this.modal.querySelector('[data-role="modal-description"]');
      const scrollEl = this.modal.querySelector('.byob-modal__scroll');

      // Instant open with what the card already knows; fetched data fills in
      this.modalCard = card;
      const cardVariant = this.selectedVariant(card);
      if (titleEl) titleEl.textContent = card.el.querySelector('.byob-card__title')?.textContent || '';
      if (priceEl && cardVariant) priceEl.textContent = cardVariant.price_formatted || this.formatMoney(cardVariant.price);
      if (descEl) descEl.innerHTML = '';
      if (scrollEl) scrollEl.scrollTop = 0;
      this.buildGallery(card.imageEl && card.imageEl.src ? [card.imageEl.src] : []);

      this.modal.showModal();
      this.modal.classList.add('is-open');

      try {
        const product = await this.fetchProduct(card.handle);
        if (this.modalCard !== card || !this.modal.open) return; // stale response

        if (titleEl) titleEl.textContent = product.title;
        if (descEl) descEl.innerHTML = product.description || '';

        const images = (product.images || []).slice(0, 8);
        if (images.length) this.buildGallery(images);
      } catch (e) {
        /* modal already shows the card-level info; fetch failure is non-fatal */
      }
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.classList.remove('is-open');
      this.modal.close();
      this.modalCard = null;
    }

    /** Renders the swipeable slide track + thumbnail strip for the given image URLs. */
    buildGallery(images) {
      const track = this.modal && this.modal.querySelector('[data-role="modal-track"]');
      const thumbsEl = this.modal && this.modal.querySelector('[data-role="modal-thumbs"]');
      if (!track) return;

      track.innerHTML = images
        .map(
          (src) =>
            '<div class="byob-modal__slide"><img src="' + this.sizedImage(src, 600) + '" alt="" loading="lazy"></div>'
        )
        .join('');
      track.scrollLeft = 0;

      if (thumbsEl) {
        thumbsEl.innerHTML =
          images.length > 1
            ? images
                .map(
                  (src, i) =>
                    '<button type="button" class="byob-modal__thumb' + (i === 0 ? ' is-active' : '') +
                    '" data-index="' + i + '"><img src="' + this.sizedImage(src, 120) + '" alt="" loading="lazy"></button>'
                )
                .join('')
            : '';
      }
    }

    goToSlide(index) {
      const track = this.modal && this.modal.querySelector('[data-role="modal-track"]');
      if (!track) return;
      track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' });
      this.setActiveThumb(index);
    }

    setActiveThumb(index) {
      if (!this.modal) return;
      this.modal.querySelectorAll('.byob-modal__thumb').forEach((t) => {
        t.classList.toggle('is-active', Number(t.dataset.index) === index);
      });
    }

    sizedImage(src, width) {
      if (!src) return src;
      return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
    }

    async fetchProduct(handle) {
      if (this.productCache[handle]) return this.productCache[handle];
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(root + 'products/' + handle + '.js');
      if (!response.ok) throw new Error('Product fetch failed');
      const json = await response.json();
      this.productCache[handle] = json;
      return json;
    }

    onChange(event) {
      const select = event.target.closest('[data-role="card-select"]');
      if (!select) return;
      const card = this.cards.find((c) => c.selectEl === select);
      if (card) {
        this.applyCardVariant(card);
        this.recompute();
      }
    }

    accessoryCount() {
      return this.cards.reduce((sum, card) => sum + (card.el.hidden ? 0 : card.qty), 0);
    }

    recompute() {
      const heroCount = this.heroVariant ? 1 : 0;
      const accessoryCount = this.accessoryCount();
      const itemCount = heroCount + accessoryCount;

      let subtotal = this.heroVariant ? this.heroVariant.price : 0;
      this.cards.forEach((card) => {
        if (card.el.hidden || card.qty === 0) return;
        const variant = this.selectedVariant(card);
        if (variant) subtotal += variant.price * card.qty;
      });

      const activeTier = [...this.tiers].reverse().find((t) => itemCount >= t.qty) || null;
      const nextTier = this.tiers.find((t) => itemCount < t.qty) || null;

      // Progress bar + markers. The NEXT milestone pulses as the target;
      // a milestone crossed for the first time this session plays a
      // pop/burst celebration (is-just-reached, removed after the animation).
      const maxQty = this.tiers.length ? this.tiers[this.tiers.length - 1].qty : 1;
      if (this.fillEl) {
        this.fillEl.style.width = Math.min(100, (itemCount / maxQty) * 100) + '%';
      }
      this.reachedQtys = this.reachedQtys || new Set();
      const upcomingQty = nextTier ? nextTier.qty : null;
      let justUnlocked = false;
      this.querySelectorAll('.byob-tiers__marker').forEach((marker) => {
        const markerQty = Number(marker.dataset.tierQty);
        const reached = itemCount >= markerQty;
        marker.classList.toggle('is-reached', reached);
        marker.classList.toggle('is-next', !reached && markerQty === upcomingQty && Boolean(this.heroVariant));
        if (reached && !this.reachedQtys.has(markerQty)) {
          this.reachedQtys.add(markerQty);
          justUnlocked = true;
          marker.classList.add('is-just-reached');
          setTimeout(() => marker.classList.remove('is-just-reached'), 900);
        } else if (!reached && this.reachedQtys.has(markerQty)) {
          this.reachedQtys.delete(markerQty);
        }
      });

      // Hint copy (flashes when a tier was just unlocked)
      if (this.hintEl && justUnlocked) {
        this.hintEl.classList.add('is-celebrate');
        setTimeout(() => this.hintEl.classList.remove('is-celebrate'), 700);
      }
      if (justUnlocked && activeTier) {
        this.track('byob_tier_reached', {
          tier_percentage: activeTier.pct,
          item_count: itemCount,
        });
      }
      if (this.hintEl) {
        if (activeTier && !nextTier) {
          this.hintEl.textContent = 'You’ve unlocked the maximum ' + activeTier.pct + '% off!';
        } else if (activeTier && nextTier) {
          const more = nextTier.qty - itemCount;
          this.hintEl.textContent =
            'You’ve unlocked ' + activeTier.pct + '% off — add ' + more + ' more ' +
            (more === 1 ? 'item' : 'items') + ' for ' + nextTier.pct + '% off';
        } else if (nextTier) {
          const more = nextTier.qty - itemCount;
          this.hintEl.textContent =
            'Add ' + more + ' more ' + (more === 1 ? 'item' : 'items') + ' to unlock ' + nextTier.pct + '% off';
        }
      }

      // Bundle tile live price (only meaningful once accessories are chosen)
      if (this.bundlePriceEl) {
        this.bundlePriceEl.textContent = accessoryCount > 0 ? this.formatMoney(subtotal) : '';
      }

      // "N selected" on the collapsed universal-accessories accordion, so
      // selections inside it stay visible even when it's closed.
      if (this.moreMetaEl && this.moreGrid) {
        const selectedInMore = this.cards.reduce(
          (sum, card) => sum + (card.el.parentElement === this.moreGrid && !card.el.hidden ? card.qty : 0),
          0
        );
        this.moreMetaEl.textContent = selectedInMore > 0 ? selectedInMore + ' selected' : '';
      }

      // Summary — with an active tier (and savings display enabled), show the
      // full subtotal struck through, the discounted total in red, and a
      // "You save $X (Y%)" line beneath.
      const countEl = this.querySelector('[data-role="summary-count"]');
      const totalEl = this.querySelector('[data-role="summary-total"]');
      const compareEl = this.querySelector('[data-role="summary-compare"]');
      const saveEl = this.querySelector('[data-role="summary-save"]');

      const showDiscount = this.showSavings && activeTier && subtotal > 0;
      const discounted = showDiscount ? Math.round(subtotal * (1 - activeTier.pct / 100)) : subtotal;

      if (countEl) countEl.textContent = itemCount + (itemCount === 1 ? ' item' : ' items');
      if (totalEl) {
        totalEl.textContent = this.formatMoney(discounted);
        totalEl.classList.toggle('is-discounted', Boolean(showDiscount));
      }
      if (compareEl) {
        compareEl.hidden = !showDiscount;
        if (showDiscount) compareEl.textContent = this.formatMoney(subtotal);
      }
      if (saveEl) {
        // Compose the line from whatever the customer has unlocked:
        //  - discount tier:  "You save A$4.64 (5%)"
        //  - free shipping:  "FREE shipping"  (evaluated on the discounted
        //    total, since Shopify checks the free-ship threshold AFTER the
        //    discount is applied — so a big discount can drop them back under)
        //  - both:           "You save A$4.64 (5%) + FREE shipping"
        const parts = [];
        if (showDiscount) {
          parts.push('You save ' + this.formatMoney(subtotal - discounted) + ' (' + activeTier.pct + '%)');
        }
        const freeShipReached =
          this.freeShipThreshold > 0 && discounted >= this.freeShipThreshold && itemCount > 0;
        if (freeShipReached) parts.push(this.freeShipLabel);

        saveEl.hidden = parts.length === 0;
        // Only-free-shipping reads oddly as a bare "FREE shipping"; give it a verb.
        saveEl.textContent =
          showDiscount ? parts.join(' + ') : freeShipReached ? this.freeShipLabel + ' unlocked' : '';
      }

      // CTA — enabled as soon as a variant is resolved. Zero accessories is a
      // valid bundle (= just the Catchy): a customer who expanded the bundle
      // panel shouldn't have to find their way back to the "Catchy Only" tile
      // to be allowed to buy. With only the hero in the selection it's not a
      // bundle, so the single-item label ("Add to cart · 1 item") is used.
      if (this.atcButton) {
        this.atcButton.disabled = !this.heroVariant;
      }
      if (this.atcLabelEl) {
        const template =
          itemCount <= 1
            ? this.atcLabelEl.dataset.labelSingle || this.atcLabelTemplate
            : this.atcLabelTemplate;
        this.atcLabelEl.textContent = template
          .replace('[count] items', itemCount === 1 ? '1 item' : itemCount + ' items')
          .replace('[count]', String(itemCount));
      }
    }

    async addToCart() {
      if (!this.heroVariant || this.atcButton.classList.contains('is-loading')) return;

      const items = [{ id: this.heroVariant.id, quantity: 1, properties: { _byob: 'true' } }];
      let bundleValue = this.heroVariant.price;
      this.cards.forEach((card) => {
        if (card.el.hidden || card.qty === 0) return;
        const variant = this.selectedVariant(card);
        if (variant) {
          items.push({ id: variant.id, quantity: card.qty, properties: { _byob: 'true' } });
          bundleValue += variant.price * card.qty;
        }
      });

      // Capture funnel values before the cards get reset on success.
      const bundleItemCount = items.reduce((sum, item) => sum + item.quantity, 0);
      const activeTier = [...this.tiers].reverse().find((t) => bundleItemCount >= t.qty) || null;

      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);

      this.atcButton.classList.add('is-loading');
      this.atcButton.disabled = true;
      if (this.errorEl) this.errorEl.hidden = true;

      try {
        const addUrl =
          (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items,
            sections: sectionIds.join(','),
            sections_url: window.location.pathname,
          }),
        });
        const json = await response.json();

        if (!response.ok || json.status) {
          throw new Error(json.description || json.message || 'Could not add the bundle to your cart.');
        }

        // The /cart/add.js response only contains the newly-added lines — it has
        // no whole-cart item_count or totals. The cart drawer reads `resource`
        // to render, so passing the add-response alone makes it show its empty
        // state. Fetch the full cart and pass THAT as the resource (this mirrors
        // the theme's own add-to-cart flow).
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        let cart = null;
        try {
          const cartRes = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
          if (cartRes.ok) cart = await cartRes.json();
        } catch (e) {
          /* fall back to the add response below */
        }

        const itemCount = cart ? cart.item_count : items.reduce((sum, item) => sum + item.quantity, 0);
        this.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart || json,
              sourceId: this.id || 'byob-builder',
              data: { source: 'byob-builder', itemCount, sections: json.sections },
            },
          })
        );

        // Analytics: bundle conversion + a GA4-standard add_to_cart so ad pixels
        // and GA4 count the bundle add (which bypasses the theme's own ATC event).
        const currency = this.dataset.currency || 'AUD';
        this.track('byob_add_to_cart', {
          item_count: bundleItemCount,
          bundle_value: bundleValue / 100,
          currency: currency,
          tier_percentage: activeTier ? activeTier.pct : 0,
        });
        try {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'add_to_cart',
            ecommerce: { currency: currency, value: bundleValue / 100, items: items.map((i) => ({ item_id: i.id, quantity: i.quantity })) },
          });
        } catch (e) {
          /* no dataLayer */
        }

        this.cards.forEach((card) => {
          card.qty = 0;
          if (card.qtyEl) card.qtyEl.textContent = '0';
          card.el.classList.remove('is-selected');
        });
        this.recompute();

        const originalLabel = this.atcLabelEl ? this.atcLabelEl.textContent : '';
        if (this.atcLabelEl) this.atcLabelEl.textContent = 'Added ✓';
        setTimeout(() => {
          if (this.atcLabelEl) this.atcLabelEl.textContent = originalLabel;
          this.recompute();
        }, 2000);
      } catch (error) {
        if (this.errorEl) {
          this.errorEl.textContent = error.message;
          this.errorEl.hidden = false;
        }
      } finally {
        this.atcButton.classList.remove('is-loading');
        this.recompute();
      }
    }
  }

  customElements.define('byob-builder', ByobBuilder);
})();
